import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api, refreshSession } from '../../lib/api';
import { useAuth, useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { type Department, KEYS, kindLabel, type OrganizationFields, type PlatformOrganization, useOrganizations } from './data';
import { OrganizationFieldset } from './OrganizationFieldset';

type Selection = { kind: 'ninguna' } | { kind: 'nueva' } | { kind: 'org'; id: string };

const EMPTY: OrganizationFields = { name: '', alias: null, kind: 'linea', groupName: '', timezone: 'America/Panama', countryCode: null };

/** Organizaciones y departamentos del tenant (guía 4.1 a 4.3). En el tenant operador, también la semilla. */
export function OrganizationsPage() {
  const me = useMe();
  const { reloadMe } = useAuth();
  const qc = useQueryClient();
  const orgs = useOrganizations();
  const [selection, setSelection] = useState<Selection>({ kind: 'ninguna' });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [busy, setBusy] = useState(false);

  const groups = [...new Set((orgs.data ?? []).map((o) => o.groupName))];
  const selected = selection.kind === 'org' ? orgs.data?.find((o) => o.id === selection.id) : undefined;

  /** Después de crear organizaciones con SuperAdmin: la sesión debe incluirlas (el contexto viaja en el token). */
  async function refreshAccess() {
    await qc.invalidateQueries({ queryKey: KEYS.organizaciones });
    await qc.invalidateQueries({ queryKey: ['acceso'] });
    if (await refreshSession()) await reloadMe();
  }

  async function loadSeed() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ organizationsCreated: number; departmentsCreated: number; positionsCreated: number; ticketCatalogsCreated: number }>(
        '/api/plataforma/semilla',
        { method: 'POST' },
      );
      await refreshAccess();
      setNotice(
        t('plataforma.semilla_resultado', {
          orgs: r.organizationsCreated,
          deps: r.departmentsCreated,
          pos: r.positionsCreated,
          tic: r.ticketCatalogsCreated,
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      setConfirmSeed(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('plataforma.org_titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('plataforma.org_descripcion')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {me.platformOperator && (
            <Button variant="secondary" disabled={busy} onClick={() => setConfirmSeed(true)}>
              {t('plataforma.semilla')}
            </Button>
          )}
          <Button
            onClick={() => {
              setNotice(null);
              setSelection({ kind: 'nueva' });
            }}
          >
            {t('plataforma.nueva_org')}
          </Button>
        </div>
      </div>
      {confirmSeed && (
        <Alert tone="warning">
          <p>{t('plataforma.semilla_confirmar')}</p>
          <div className="mt-2 flex gap-2">
            <Button disabled={busy} onClick={() => void loadSeed()}>
              {t('comun.continuar')}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmSeed(false)}>
              {t('comun.cancelar')}
            </Button>
          </div>
        </Alert>
      )}
      {(error ?? orgs.error) && <Alert>{error ?? errorMessage(orgs.error)}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {!orgs.data ? (
        !orgs.error && <Spinner label={t('comun.cargando')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
          <Card className="self-start p-2">
            {groups.map((g) => (
              <div key={g} className="mb-2">
                <h2 className="px-3 pb-1 pt-2 text-xs font-semibold text-slate-500">{g}</h2>
                <ul aria-label={g}>
                  {orgs.data
                    .filter((o) => o.groupName === g)
                    .map((o) => (
                      <li key={o.id}>
                        <button
                          type="button"
                          aria-current={selected?.id === o.id ? 'true' : undefined}
                          className={`w-full rounded px-3 py-2 text-left hover:bg-slate-50 ${selected?.id === o.id ? 'bg-slate-100' : ''}`}
                          onClick={() => {
                            setNotice(null);
                            setSelection({ kind: 'org', id: o.id });
                          }}
                        >
                          <span className="flex flex-wrap items-center gap-1 text-sm font-medium text-slate-900">
                            {o.name}
                            {!o.inSession && (
                              <span title={t('plataforma.org_sin_acceso_ayuda')}>
                                <Badge tone="amber">{t('plataforma.org_sin_acceso')}</Badge>
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {kindLabel(o.kind)} · {t('plataforma.org_resumen', { deps: o.departments, users: o.users })}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </Card>

          {selection.kind === 'nueva' || selected ? (
            <div className="space-y-4">
              <OrganizationEditor
                key={selected?.id ?? 'nueva'}
                org={selected ?? null}
                groups={groups}
                onDone={async (message, id, grantedAccess) => {
                  if (grantedAccess) await refreshAccess();
                  else await qc.invalidateQueries({ queryKey: KEYS.organizaciones });
                  setNotice(message);
                  setSelection({ kind: 'org', id });
                }}
              />
              {selected && <Departments key={selected.id} org={selected} />}
            </div>
          ) : (
            <p className="text-sm text-slate-600">{t('plataforma.elegir_org')}</p>
          )}
        </div>
      )}
    </div>
  );
}

function OrganizationEditor({
  org,
  groups,
  onDone,
}: {
  org: PlatformOrganization | null;
  groups: string[];
  onDone: (message: string, id: string, grantedAccess: boolean) => Promise<void>;
}) {
  const [value, setValue] = useState<OrganizationFields>(() =>
    org ? { name: org.name, alias: org.alias, kind: org.kind, groupName: org.groupName, timezone: org.timezone, countryCode: org.countryCode } : EMPTY,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { ...value, name: value.name.trim(), alias: value.alias?.trim() || null, groupName: value.groupName.trim(), timezone: value.timezone.trim() };
    try {
      if (org) {
        await api(`/api/plataforma/organizaciones/${org.id}`, { method: 'PATCH', json: body });
        await onDone(t('plataforma.org_guardada'), org.id, false);
      } else {
        const r = await api<{ id: string; superadminGranted: boolean }>('/api/plataforma/organizaciones', { json: body });
        await onDone(r.superadminGranted ? t('plataforma.org_creada_superadmin') : t('plataforma.org_creada'), r.id, r.superadminGranted);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={org ? org.name : t('plataforma.nueva_org')}>
      <form onSubmit={(e) => void save(e)} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <OrganizationFieldset idPrefix="org" value={value} onChange={setValue} groups={groups} />
        <Button type="submit" disabled={busy || !value.name.trim() || !value.groupName.trim() || !value.timezone.trim()}>
          {t('comun.guardar')}
        </Button>
      </form>
    </Card>
  );
}

function Departments({ org }: { org: PlatformOrganization }) {
  const qc = useQueryClient();
  const key = KEYS.departamentos(org.id);
  const departments = useQuery({ queryKey: key, queryFn: () => api<Department[]>(`/api/plataforma/organizaciones/${org.id}/departamentos`) });
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState<Department | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const base = `/api/plataforma/organizaciones/${org.id}/departamentos`;

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      await qc.invalidateQueries({ queryKey: KEYS.organizaciones }); // el conteo de la lista
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const add = (e: FormEvent) => {
    e.preventDefault();
    return act(async () => {
      await api(base, { json: { name: name.trim() } });
      setName('');
    });
  };

  return (
    <Card title={t('plataforma.departamentos')}>
      <div className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {!departments.data ? (
          departments.error ? <Alert>{errorMessage(departments.error)}</Alert> : <Spinner label={t('comun.cargando')} />
        ) : departments.data.length === 0 ? (
          <p className="text-sm text-slate-500">{t('plataforma.sin_departamentos')}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('plataforma.departamentos')}>
            {departments.data.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                {editing?.id === d.id ? (
                  <form
                    className="flex flex-1 flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void act(async () => {
                        await api(`${base}/${d.id}`, { method: 'PATCH', json: { name: editing.name.trim() } });
                        setEditing(null);
                      });
                    }}
                  >
                    <div className="min-w-48 flex-1">
                      <Field id={`dep-${d.id}`} label={t('plataforma.renombrar')} value={editing.name} maxLength={80} autoFocus onChange={(e) => setEditing({ id: d.id, name: e.target.value })} />
                    </div>
                    <Button type="submit" disabled={busy || !editing.name.trim()}>
                      {t('comun.guardar')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                      {t('comun.cancelar')}
                    </Button>
                  </form>
                ) : (
                  <>
                    <div>
                      <span className="text-sm font-medium text-slate-900">{d.name}</span>
                      <span className="block text-xs text-slate-500">{t('plataforma.dep_usuarios', { n: d.users })}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.renombrar')} ${d.name}`} onClick={() => setEditing({ id: d.id, name: d.name })}>
                        {t('plataforma.renombrar')}
                      </Button>
                      <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.borrar')} ${d.name}`} onClick={() => setConfirming(d)}>
                        {t('plataforma.borrar')}
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {confirming && (
          <Alert tone="warning">
            <p>{t('plataforma.dep_borrar_confirmar', { nombre: confirming.name })}</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api(`${base}/${confirming.id}`, { method: 'DELETE' });
                  }).finally(() => setConfirming(null))
                }
              >
                {t('acceso.si_borrar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        )}
        <form onSubmit={(e) => void add(e)} className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Field id="nuevo-departamento" label={t('plataforma.nuevo_departamento')} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy || !name.trim()}>
            {t('plataforma.agregar')}
          </Button>
        </form>
      </div>
    </Card>
  );
}
